import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import logo from '../../assets/logo.png';
import { FaBars, FaTimes, FaHome, FaLaptop, FaUsers, FaEnvelope, FaProjectDiagram } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';


const HeaderLogo = styled.img`
  height: 50px;
  width: auto;
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(1.05);
  }
`;

const HeaderContainer = styled.header`
  position: fixed;
  width: 100%;
  top: 0;
  left: 0;
  background: rgba(255, 255, 255, 0.95);
  z-index: 1000;
  box-shadow: 0 2px 5px rgba(0,0,0,0.1);

  .logo-container {
    display: flex;
    align-items: center;
  }
`;

const Nav = styled.nav`
  max-width: 1200px;
  margin: 0 auto;
  padding: 1rem 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const NavLinks = styled.div`
  display: flex;
  gap: 2rem;
  text-decoration: none;
  color: #333;
  font-weight: 500;
  font-family: 'DM Sans', sans-serif;
  transition: color 0.3s ease;
  text-transform: uppercase;

  &:hover {
    color: #007bff;
  }
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const NavLink = styled(Link)`
  text-decoration: none;
  color: #333;
  font-weight: 500;
  transition: color 0.3s ease;
  
  &:hover {
    color: #007bff;
  }
`;

// Botón de menú hamburguesa con animación
const MobileMenuButton = styled(motion.button)`
  display: none;
  background: transparent;
  border: none;
  color: #333;
  font-size: 1.8rem;
  cursor: pointer;
  padding: 0.5rem;
  
  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

// Menú móvil con animaciones
const MobileMenuOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  right: 0;
  width: 100%;
  height: 100vh;
  background: linear-gradient(135deg, rgb(2, 36, 109) 0%, rgb(0, 78, 146) 100%);
  z-index: 2000;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

// Fondo con partículas
const ParticlesBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0.2;
  background-image: 
    radial-gradient(circle at 25% 25%, rgba(255, 255, 255, 0.2) 1px, transparent 1px),
    radial-gradient(circle at 75% 75%, rgba(255, 255, 255, 0.2) 1px, transparent 1px);
  background-size: 50px 50px;
  z-index: 0;
`;

const CloseButton = styled(motion.button)`
  position: absolute;
  top: 20px;
  right: 20px;
  background: rgba(255, 255, 255, 0.1);
  border: none;
  color: white;
  font-size: 1.5rem;
  cursor: pointer;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
`;

const MobileNavLinks = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2rem;
  z-index: 1;
`;

const MobileNavLink = styled(motion.div)`
  position: relative;
  
  a {
    display: flex;
    align-items: center;
    gap: 1rem;
    text-decoration: none;
    color: white;
    font-size: 1.5rem;
    font-weight: 500;
    font-family: 'DM Sans', sans-serif;
    text-transform: uppercase;
    padding: 0.5rem 1.5rem;
    border-radius: 30px;
    transition: all 0.3s ease;
    
    &:hover {
      background: rgba(255, 255, 255, 0.1);
      transform: translateY(-3px);
    }
    
    svg {
      font-size: 1.2rem;
    }
  }
`;

// Decoración visual
const Decoration = styled(motion.div)`
  position: absolute;
  width: 300px;
  height: 300px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(0, 123, 255, 0.2) 0%, rgba(0, 123, 255, 0) 70%);
  z-index: 0;
  
  &.top-right {
    top: -100px;
    right: -100px;
  }
  
  &.bottom-left {
    bottom: -100px;
    left: -100px;
  }
`;

// Variantes para animaciones
const overlayVariants = {
  closed: { 
    x: "100%",
    transition: {
      type: "spring",
      damping: 20,
      stiffness: 100
    }
  },
  open: { 
    x: 0,
    transition: {
      type: "spring",
      damping: 20,
      stiffness: 80,
      staggerChildren: 0.1,
      delayChildren: 0.2
    }
  }
};

const itemVariants = {
  closed: { 
    opacity: 0,
    y: 20,
    transition: {
      duration: 0.2
    }
  },
  open: { 
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4
    }
  }
};

const decorationVariants = {
  closed: {
    scale: 0,
    opacity: 0
  },
  open: {
    scale: 1,
    opacity: 1,
    transition: {
      duration: 0.8,
      delay: 0.2
    }
  }
};

function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  
  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
    document.body.style.overflow = isMenuOpen ? 'auto' : 'hidden';
  };
  
  useEffect(() => {
    setIsMenuOpen(false);
    document.body.style.overflow = 'auto';
  }, [location]);
  
  return (
    <>
      <HeaderContainer>
        <Nav>
          <Link to="/" className="logo-container">
            <HeaderLogo src={logo} alt="Juan Fra Hernández" />
          </Link>
          <NavLinks>
            <NavLink to="/">Inicio</NavLink>
            <NavLink to="/proyectos">Proyectos</NavLink>
            <NavLink to="/servicios">Servicios</NavLink>
            <NavLink to="/quienes-somos">Quiénes Somos</NavLink>
            <NavLink to="/contacto">Contacto</NavLink>
          
          </NavLinks>
          <MobileMenuButton 
            onClick={toggleMenu}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            <FaBars />
          </MobileMenuButton>
        </Nav>
      </HeaderContainer>
      
      <AnimatePresence>
        {isMenuOpen && (
          <MobileMenuOverlay
            initial="closed"
            animate="open"
            exit="closed"
            variants={overlayVariants}
          >
            <ParticlesBackground />
            
            <Decoration 
              className="top-right" 
              variants={decorationVariants}
            />
            <Decoration 
              className="bottom-left" 
              variants={decorationVariants}
            />
            
            <CloseButton 
              onClick={toggleMenu}
              whileHover={{ scale: 1.1, rotate: 90 }}
              whileTap={{ scale: 0.9 }}
            >
              <FaTimes />
            </CloseButton>
            
            <MobileNavLinks>
              <MobileNavLink variants={itemVariants}>
                <Link to="/" onClick={toggleMenu}>
                  <FaHome /> Inicio
                </Link>
              </MobileNavLink>
              <MobileNavLink variants={itemVariants}>
                <Link to="/proyectos" onClick={toggleMenu}>
                  <FaProjectDiagram /> Proyectos
                </Link>
              </MobileNavLink>
              <MobileNavLink variants={itemVariants}>
                <Link to="/servicios" onClick={toggleMenu}>
                  <FaLaptop /> Servicios
                </Link>
              </MobileNavLink>
              <MobileNavLink variants={itemVariants}>
                <Link to="/quienes-somos" onClick={toggleMenu}>
                  <FaUsers /> Quiénes Somos
                </Link>
              </MobileNavLink>
              <MobileNavLink variants={itemVariants}>
                <Link to="/contacto" onClick={toggleMenu}>
                  <FaEnvelope /> Contacto
                </Link>
              </MobileNavLink>
              
            </MobileNavLinks>
          </MobileMenuOverlay>
        )}
      </AnimatePresence>
    </>
  );
}

export default Header;